import { useEffect, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';
import type { PersistedAgentConfig } from '@/types/agent';

import { AgentCard } from './AgentCard';
import { AgentDetailPanel } from './AgentDetailPanel';

export const DemoAgents = () => {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const [agents, setAgents] = useState<PersistedAgentConfig[]>([]);
	const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const fetchAgents = async () => {
			if (!userId) return;

			try {
				setLoading(true);
				setError(null);
				const response = await bridgewayApi.get<PersistedAgentConfig[]>(
					`/api/user/${userId}/agents/`,
				);
				setAgents(response);
			} catch (err) {
				logger.error('Failed to fetch agents', err);
				setError(err instanceof Error ? err.message : 'Failed to fetch agents');
			} finally {
				setLoading(false);
			}
		};

		fetchAgents();
	}, [userId, bridgewayApi]);

	const selectedAgent = selectedAgentId
		? agents.find((a) => a.id === selectedAgentId)
		: null;

	if (selectedAgent) {
		return (
			<AgentDetailPanel
				agent={selectedAgent}
				onBack={() => setSelectedAgentId(null)}
			/>
		);
	}

	return (
		<div
			style={{
				minHeight: '100vh',
				background: '#F7F7F7',
				padding: '2rem 1.5rem',
			}}
		>
			<div style={{ maxWidth: '1280px', margin: '0 auto' }}>
				<h1
					style={{
						fontSize: '2rem',
						fontWeight: 700,
						color: 'var(--text-primary)',
						marginBottom: '2rem',
						letterSpacing: '-0.02em',
					}}
				>
					Agents
				</h1>

				{loading && (
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
							gap: '1.5rem',
						}}
					>
						{[1, 2, 3].map((i) => (
							<div
								key={i}
								className="glass-card"
								style={{
									padding: '1.5rem',
									height: '200px',
									background: '#ffffff',
								}}
							>
								<div
									style={{
										width: '60%',
										height: '1.5rem',
										background: '#e5e7eb',
										borderRadius: '4px',
										marginBottom: '1rem',
									}}
								/>
								<div
									style={{
										width: '100%',
										height: '3rem',
										background: '#f3f4f6',
										borderRadius: '4px',
										marginBottom: '0.75rem',
									}}
								/>
								<div
									style={{
										width: '40%',
										height: '1rem',
										background: '#e5e7eb',
										borderRadius: '4px',
									}}
								/>
							</div>
						))}
					</div>
				)}

				{error && (
					<div
						className="glass-card"
						style={{
							padding: '1.5rem',
							background: '#fef2f2',
							border: '1px solid #fecaca',
						}}
					>
						<p style={{ color: '#dc2626', fontSize: '0.875rem' }}>{error}</p>
					</div>
				)}

				{!loading && !error && agents.length === 0 && (
					<div
						className="glass-card"
						style={{
							padding: '3rem',
							textAlign: 'center',
							background: '#ffffff',
						}}
					>
						<p
							style={{
								fontSize: '1.125rem',
								color: 'var(--text-secondary)',
								marginBottom: '0.5rem',
							}}
						>
							No agents yet
						</p>
						<p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
							Agents will appear here after onboarding completes
						</p>
					</div>
				)}

				{!loading && !error && agents.length > 0 && (
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
							gap: '1.5rem',
						}}
					>
						{agents.map((agent, index) => (
							<AgentCard
								key={agent.id}
								agent={agent}
								onClick={() => setSelectedAgentId(agent.id)}
								staggerIndex={index + 1}
							/>
						))}
					</div>
				)}
			</div>
		</div>
	);
};
